import { FC, useEffect, useState } from 'react'
import { FormikProps, FormikValues } from 'formik'
import { useAppSelector } from '../../../../../store/hooks'
import { BuilderPage, PreviewTheme, Choice, DevicePreview, CtaType } from '../types'
import { hexToRgb } from '../utils/helper'
import { Language } from '../../../../../features/security/security.slice'
import RenderTranslatedFields from '../../../../common/forms/fields/translated-fields/RenderTranslatedFields'
import HeaderCtaDialog from '../../../../common/dialogs/HeaderCtaDialog'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlusCircle, faHeart, faUsers } from '@fortawesome/free-solid-svg-icons'

interface CanvasHeaderProps {
    page: BuilderPage
    previewTheme: PreviewTheme | null
    formikProps: FormikProps<FormikValues>
    availablePages: Choice[]
    deletedCtas: string[]
    setDeletedCtas: React.Dispatch<React.SetStateAction<string[]>>
    headerSizeCodes: Record<string, string>
    translations: Record<string, unknown>
    activeLocale: string
    devicePreview: DevicePreview
}

const CanvasHeader: FC<CanvasHeaderProps> = ({
    page,
    previewTheme,
    formikProps,
    availablePages,
    deletedCtas,
    setDeletedCtas,
    headerSizeCodes,
    translations,
    activeLocale,
    devicePreview,
}) => {
    const theme = useAppSelector((state) => state.theme.theme)
    const initData = useAppSelector((state: any) => state.security.initData)
    const [headerHeight, setHeaderHeight] = useState('24rem')
    const [ctaHovered, setCtaHovered] = useState(false)

    useEffect(() => {
        const hSize =
            headerSizeCodes[
                formikProps.values.header_size_id as string
            ] || 'H'
        switch (hSize) {
            case 'Q':
                setHeaderHeight('16rem')
                break
            case 'H':
                setHeaderHeight('24rem')
                break
            default:
                setHeaderHeight('32rem')
                break
        }
    }, [formikProps.values.header_size_id, headerSizeCodes])

    const primaryHex = previewTheme?.colors?.primary?.hex || '#c71c69'
    const secondaryHex = previewTheme?.colors?.secondary?.hex || '#222222'
    const headerImageUrl =
        page.header_image_id
            ? `${import.meta.env.VITE_API_ROOT_URL}/media/download/${page.header_image_id}`
            : null

    const bgStyle = headerImageUrl
        ? `linear-gradient(165deg, ${secondaryHex} 0%, rgba(${hexToRgb(primaryHex)}, 0.85) 50%, ${secondaryHex} 100%), url(${headerImageUrl})`
        : `linear-gradient(165deg, ${secondaryHex} 0%, rgba(${hexToRgb(primaryHex)}, 0.7) 40%, ${secondaryHex} 100%)`

    const isMobile = devicePreview === 'mobile'

    const { values } = formikProps
    const ctas = values.ctas ? Object.values(values.ctas) as CtaType[] : []
    const activeCtas = ctas.filter(
        (cta) => cta.active && !deletedCtas.includes(cta.id || '')
    )
    const inactiveCtas = ctas.filter(
        (cta) => !cta.active || deletedCtas.includes(cta.id || '')
    )

    const langId = activeLocale
        ? initData.languages.data[activeLocale]?.id
        : null

    const getCtaLabel = (cta: CtaType) => {
        if (!langId || !cta.id) return ''
        const labelKey = `header_cta_${cta.id}_label_${langId}`
        const translated = (cta as unknown as Record<string, unknown>)[labelKey] as string
        return translated || ''
    }

    return (
        <header
            className="sfy-canvas-header"
            style={{
                minHeight: headerHeight,
                backgroundImage: bgStyle,
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                backgroundSize: 'cover',
            }}
        >
            <div className="sfy-canvas-header__pattern" />
            <div className="sfy-canvas-header__spotlight" />

            <div className={`sfy-canvas-header__content ${isMobile ? 'sfy-canvas-header__content--mobile' : ''}`}>
                <div className="sfy-canvas-header__fields">
                    <RenderTranslatedFields
                        id="builderPageHeadersTranslatedField"
                        tabGroupClassName="bg-transparent"
                        tabContentClassName="bg-transparent"
                        formikProps={formikProps}
                        activeLocale={activeLocale}
                        fields={[
                            {
                                id: 'builderPageHeaderTop',
                                type: 'text',
                                name: 'main_header_top',
                                className:
                                    'sfy-canvas-header__field-badge bg-transparent text-white border-0',
                                placeholder: 'Badge text (e.g. "Welcome")',
                            },
                            {
                                id: 'builderPageHeaderMiddle',
                                type: 'text',
                                name: 'main_header_middle_big',
                                className:
                                    'sfy-canvas-header__field-headline bg-transparent text-white border-0',
                                placeholder: 'Main headline',
                            },
                            {
                                id: 'builderPageHeaderBottom',
                                type: 'text',
                                name: 'main_header_bottom',
                                className:
                                    'sfy-canvas-header__field-subtext bg-transparent text-white border-0',
                                placeholder: 'Supporting text',
                            },
                        ]}
                    />
                </div>

                <div
                    className="sfy-canvas-header__ctas"
                    onMouseEnter={() => setCtaHovered(true)}
                    onMouseLeave={() => setCtaHovered(false)}
                >
                    <div className={`sfy-cta-preview ${isMobile ? 'sfy-cta-preview--stacked' : ''}`}>
                        {activeCtas.map((cta) => (
                            <HeaderCtaDialog
                                key={cta.id}
                                cta={cta}
                                isNew={false}
                                formikProps={formikProps}
                                translations={translations}
                                availablePages={availablePages as unknown as []}
                                deletedCtas={deletedCtas as unknown as []}
                                setDeletedCtas={setDeletedCtas}
                                button={{
                                    label: (
                                        <span className="sfy-cta-preview__btn-inner">
                                            <FontAwesomeIcon
                                                icon={cta.style === 'dark' ? faHeart : faUsers}
                                                className="me-2"
                                            />
                                            {getCtaLabel(cta)}
                                        </span>
                                    ),
                                    variant: cta.style === 'dark' ? 'cta-accent' : 'cta-glass',
                                }}
                            />
                        ))}

                        {ctaHovered && inactiveCtas.map((cta) => (
                            <HeaderCtaDialog
                                key={cta.id}
                                cta={cta}
                                isNew={false}
                                formikProps={formikProps}
                                translations={translations}
                                availablePages={availablePages as unknown as []}
                                deletedCtas={deletedCtas as unknown as []}
                                setDeletedCtas={setDeletedCtas}
                                button={{
                                    label: (
                                        <span className="sfy-cta-preview__btn-inner sfy-cta-preview__btn-inner--inactive">
                                            {getCtaLabel(cta)}
                                        </span>
                                    ),
                                    variant: 'cta-inactive',
                                }}
                            />
                        ))}

                        <div className={`sfy-cta-preview__add-wrap ${ctaHovered ? 'sfy-cta-preview__add-wrap--visible' : ''}`}>
                            <HeaderCtaDialog
                                cta={{
                                    label: '',
                                    active: false,
                                    url_type: 'external',
                                    url: '',
                                    url_id: null,
                                    style: 'light',
                                    order: null,
                                }}
                                isNew
                                formikProps={formikProps}
                                translations={translations}
                                availablePages={availablePages as unknown as []}
                                deletedCtas={deletedCtas as unknown as []}
                                setDeletedCtas={setDeletedCtas}
                                button={{
                                    label: (
                                        <FontAwesomeIcon icon={faPlusCircle} />
                                    ),
                                    variant: 'cta-add',
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div className="sfy-canvas-header__curve">
                <svg viewBox="0 0 1440 96" fill="none" preserveAspectRatio="none" aria-hidden="true">
                    <path d="M0 96V70C240 20 480 0 720 0s480 20 720 70V96H0z" fill="currentColor" />
                </svg>
            </div>
        </header>
    )
}

export default CanvasHeader
